import { useState, useRef } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Calendar, BarChart3, User, Camera, Edit2, Check, X } from 'lucide-react'
import ReadingStats from '@/components/ReadingStats'
import { UserAvatar } from '@/components/Avatar'

export default function ProfilePage() {
  const { user, updateProfile } = useAuth()
  const [activeTab, setActiveTab] = useState<'profile' | 'stats' | 'history'>('profile')
  const [isEditingName, setIsEditingName] = useState(false)
  const [newUsername, setNewUsername] = useState(user?.username || '')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const queryClient = useQueryClient()

  const { data: readingHistory } = useQuery({
    queryKey: ['readingHistory'],
    queryFn: () => api.getReadingHistory(1, 100),
    enabled: !!user,
  })

  // 更新用户名
  const updateUsernameMutation = useMutation({
    mutationFn: (username: string) => updateProfile({ username }),
    onSuccess: () => {
      setIsEditingName(false)
      queryClient.invalidateQueries({ queryKey: ['user'] })
    },
    onError: (error: any) => {
      alert(error.message || '更新失败')
    }
  })

  // 上传头像
  const uploadAvatarMutation = useMutation({
    mutationFn: (file: File) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const base64 = reader.result as string
          updateProfile({ avatar: base64 })
            .then(() => {
              queryClient.invalidateQueries({ queryKey: ['user'] })
              resolve(base64)
            })
            .catch(reject)
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },
    onSuccess: () => {
      setIsUploading(false)
    },
    onError: (error: any) => {
      setIsUploading(false)
      alert(error.message || '上传失败')
    }
  })

  const handleAvatarUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // 检查文件大小 (限制为 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('图片大小不能超过 2MB')
      return
    }

    // 检查文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件')
      return
    }

    setIsUploading(true)
    uploadAvatarMutation.mutate(file)
  }

  const handleUsernameUpdate = () => {
    if (!newUsername.trim()) {
      alert('用户名不能为空')
      return
    }
    if (newUsername === user?.username) {
      setIsEditingName(false)
      return
    }
    updateUsernameMutation.mutate(newUsername.trim())
  }





  const tabs = [
    { key: 'profile' as const, label: '个人信息', icon: User },
    { key: 'stats' as const, label: '统计分析', icon: BarChart3 },
    { key: 'history' as const, label: '阅读历史', icon: Calendar },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 relative overflow-hidden">
      {/* 轻盈背景装饰 */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-64 h-64 bg-blue-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 left-10 w-80 h-80 bg-indigo-200/20 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-purple-100/40 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4 py-6">
        {/* 标签页导航 */}
        <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl p-4 mb-4">
          <div className="grid grid-cols-3 gap-2">
            {tabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex flex-col items-center justify-center py-2 px-2 rounded-xl font-medium transition-all duration-300 ${
                  activeTab === key
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-white/50 backdrop-blur-sm'
                }`}
              >
                <Icon className="h-4 w-4 mb-1" />
                <span className="text-xs">{label}</span>
              </button>
            ))}
          </div>
        </div>



        {/* 个人信息标签页 */}
        {activeTab === 'profile' && (
          <div className="space-y-3">
            {/* 个人信息卡片 */}
            <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden">
              {/* 头像区域 */}
              <div className="p-5 text-center">
                <div className="relative inline-block">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="relative group"
                  >
                    <UserAvatar
                      src={user?.avatar}
                      username={user?.username}
                      userId={user?.id}
                      size="xl"
                      className="rounded-2xl shadow-lg group-hover:opacity-80 transition-opacity"
                      priority={true}
                      lazy={false}
                    />

                    {/* 悬浮提示 */}
                    <div className="absolute inset-0 rounded-2xl bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <Camera className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>

                    {isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-2xl flex items-center justify-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      </div>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">点击头像更换图片</p>
                <p className="text-xs text-gray-400">支持 JPG、PNG 格式，大小不超过 2MB</p>
              </div>

              {/* 用户名区域 */}
              <div className="px-5 py-4 border-t border-gray-100/50">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-xs font-medium text-gray-900">
                      用户名
                    </label>
                    <span className="text-xs text-gray-500">排行榜显示名称</span>
                  </div>

                  {isEditingName ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        className="flex-1 px-3 py-2 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all text-sm"
                        placeholder="请输入用户名"
                        maxLength={20}
                        autoFocus
                      />
                      <button
                        onClick={handleUsernameUpdate}
                        disabled={updateUsernameMutation.isPending}
                        className="p-2 text-green-600 hover:bg-green-50/80 rounded-xl transition-colors disabled:opacity-50"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingName(false)
                          setNewUsername(user?.username || '')
                        }}
                        className="p-2 text-gray-600 hover:bg-gray-50/80 rounded-xl transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => setIsEditingName(true)}
                      className="flex items-center justify-between p-3 bg-white/50 backdrop-blur-sm border border-gray-200/50 rounded-xl cursor-pointer hover:border-blue-300/50 hover:bg-blue-50/50 transition-all group"
                    >
                      <span className="text-gray-900 font-medium text-sm">{user?.username}</span>
                      <Edit2 className="h-4 w-4 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    </div>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            </div>
          </div>
        )}

        {/* 统计分析标签页 */}
        {activeTab === 'stats' && (
          <ReadingStats records={readingHistory?.data?.data || []} />
        )}

        {/* 阅读历史标签页 */}
        {activeTab === 'history' && (
          <div className="bg-white/80 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-100/50">
              <h3 className="text-base font-semibold text-gray-900">阅读历史</h3>
            </div>
            {readingHistory?.data?.data && readingHistory.data.data.length > 0 ? (
              <div className="divide-y divide-gray-100/50 max-h-80 overflow-y-auto">
                {readingHistory.data.data.map((record: any) => (
                  <div key={record.id} className="flex items-center justify-between p-3 hover:bg-white/50 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 mb-1 text-sm">{record.date}</p>
                      {record.bookTitle && (
                        <p className="text-xs text-gray-600 mb-1">{record.bookTitle}</p>
                      )}
                      {record.notes && (
                        <p className="text-xs text-gray-500 truncate">{record.notes}</p>
                      )}
                    </div>
                    <div className="text-right ml-3">
                      <span className="text-lg font-semibold text-blue-600">{record.count}</span>
                      <span className="text-xs text-gray-500 ml-1">遍</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="mx-auto h-10 w-10 text-gray-400 mb-3" />
                <p className="text-gray-500 text-sm">还没有阅读记录</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
